(function () {
  'use strict';

  // Common selectors for the element whose real rendered height changes
  // between a theme's "full" and "compact/scrolled" header states. Tried
  // in order; the section's "Header CSS selector" setting is tried first
  // when the merchant has filled it in.
  //
  // Note: on themes with a native Shopify sticky header group, the
  // .shopify-section-group-header-group wrapper often stays reserved at
  // its full original height as a layout placeholder even once the
  // visible header inside it shrinks to a compact state — so it's listed
  // only as a last-resort fallback, after the elements whose own box
  // actually changes size.
  var FALLBACK_SELECTORS = [
    '#SiteHeader',
    '.site-header',
    'header-section',
    '#HeaderWrapper',
    '.header-wrapper',
    '.shopify-section-group-header-group',
    '#shopify-section-header',
    'header.header',
    'header'
  ];

  // Sanity clamp: guards against ever picking up something absurd (an
  // off-canvas drawer, a full-page overlay) and throwing the bar way off
  // screen. No real theme header should be taller than this.
  var MAX_REASONABLE_HEIGHT = 480;

  function findHeaderEl(customSelector) {
    if (customSelector) {
      var custom = document.querySelector(customSelector);
      if (custom) return custom;
    }
    for (var i = 0; i < FALLBACK_SELECTORS.length; i++) {
      var el = document.querySelector(FALLBACK_SELECTORS[i]);
      if (el) return el;
    }
    return null;
  }

  // A header's own bounding box only reflects its normal-flow content —
  // a row positioned absolutely inside it (a common technique for nav
  // rows, seen on this theme's "nav: below" layout) renders visually
  // lower down without ever counting toward the parent's own height. So
  // rather than trust headerEl's own rect, scan its descendants and use
  // the furthest-down edge actually being rendered on screen.
  function isActuallyVisible(elm) {
    // Closed dropdowns/megamenus on this kind of theme are often kept in
    // the DOM with real dimensions (for open/close transitions) rather
    // than collapsed to zero size — hidden only via opacity/visibility,
    // or living inside a <details> that isn't open. None of those should
    // count as part of the header's visible footprint.
    var closedDetails = elm.closest('details');
    if (closedDetails && !closedDetails.open) return false;

    var cs = getComputedStyle(elm);
    if (cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.05) return false;

    return true;
  }

  function getRenderedBottom(headerEl) {
    var maxBottom = headerEl.getBoundingClientRect().bottom;
    var descendants = headerEl.querySelectorAll('*');
    for (var i = 0; i < descendants.length; i++) {
      var el = descendants[i];
      var rect = el.getBoundingClientRect();
      // Skip elements with no visible box (display:none, empty, etc).
      if (rect.width === 0 || rect.height === 0) continue;
      if (rect.bottom <= maxBottom) continue;
      if (!isActuallyVisible(el)) continue;
      maxBottom = rect.bottom;
    }
    return maxBottom;
  }

  function TrustBarSticky(el) {
    this.el = el;
    this.isSticky = el.dataset.sticky === 'true';
    this.headerEl = null;
    this.spacer = null;

    if (!this.isSticky) return;

    this.headerEl = findHeaderEl(el.dataset.headerSelector);

    // Reserve the bar's own height in the document flow, since position:
    // fixed removes it from flow and content below would otherwise jump up.
    this.spacer = document.createElement('div');
    this.spacer.className = 'trust-bar__spacer';
    el.parentNode.insertBefore(this.spacer, el);

    this.update = this.update.bind(this);
    this.onScroll = this.onScroll.bind(this);

    window.addEventListener('resize', this.update);
    window.addEventListener('load', this.update);
    document.addEventListener('shopify:section:load', this.update);

    // The full <-> compact header switch is a scroll-triggered class
    // change on a descendant, not a resize of the header element itself,
    // so ResizeObserver alone won't catch it — a throttled scroll listener
    // is needed to re-measure at the moment it happens.
    window.addEventListener('scroll', this.onScroll, { passive: true });

    if ('ResizeObserver' in window) {
      // Fires immediately whenever the header's actual rendered box size
      // changes for any reason — including switching to a shorter
      // compact/scrolled state — without polling every frame.
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.el);
      if (this.headerEl) this.resizeObserver.observe(this.headerEl);
    }

    this.update();
  }

  TrustBarSticky.prototype.onScroll = function () {
    if (!this.scrollTicking) {
      this.scrollTicking = true;
      var self = this;
      requestAnimationFrame(function () {
        self.update();
        self.scrollTicking = false;
      });
    }

    // The header's compact <-> full swap can keep transitioning for a
    // couple hundred ms after the last scroll event fires. Re-check a
    // few more times once scrolling settles so we land on the final
    // state instead of whatever was true the instant scrolling stopped.
    clearTimeout(this.settleTimer);
    var self2 = this;
    this.settleTimer = setTimeout(function () {
      self2.update();
      setTimeout(function () { self2.update(); }, 150);
      setTimeout(function () { self2.update(); }, 350);
    }, 50);
  };

  TrustBarSticky.prototype.update = function () {
    if (!this.headerEl) {
      this.headerEl = findHeaderEl(this.el.dataset.headerSelector);
      if (this.headerEl && this.resizeObserver) {
        this.resizeObserver.observe(this.headerEl);
      }
    }

    // This is the header's rendered bottom edge (viewport-relative), which
    // doubles as exactly the "top" offset our fixed bar needs.
    var headerHeight = this.headerEl ? getRenderedBottom(this.headerEl) : 0;
    if (headerHeight < 0 || headerHeight > MAX_REASONABLE_HEIGHT) headerHeight = 0;

    this.el.style.setProperty('--tb-header-height', headerHeight + 'px');

    var barHeight = this.el.offsetHeight;
    if (this.spacer) {
      this.spacer.style.height = barHeight + 'px';
    }
  };

  function init() {
    var bars = document.querySelectorAll('trust-bar-sticky');
    bars.forEach(function (bar) {
      if (bar._trustBarInstance) return;
      bar._trustBarInstance = new TrustBarSticky(bar);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when a merchant re-renders this section in the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    var bar = event.target.querySelector && event.target.querySelector('trust-bar-sticky');
    if (bar && !bar._trustBarInstance) {
      bar._trustBarInstance = new TrustBarSticky(bar);
    }
  });
})();
